/*
 * Admin Tickets panel.
 *
 * Lists all support tickets (filterable by status), opens any of them
 * into a full thread view that reuses the same per-message rendering as
 * the player-facing Support page, and lets admins reply / change status.
 *
 * The underlying endpoints are in /api/support/admin/tickets and
 * /api/support/tickets/{id} (admin auth gets full read access via
 * is_admin check in the route).
 */
import { useEffect, useState } from "react";
import { LifeBuoy, MessageSquare, Send } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed", "all"];
const STATUS_COLOURS = {
  open:        "text-emerald-300 bg-emerald-300/10 border-emerald-300/30",
  in_progress: "text-amber-300 bg-amber-300/10 border-amber-300/30",
  resolved:    "text-sky-300 bg-sky-300/10 border-sky-300/30",
  closed:      "text-[var(--muted)] bg-white/5 border-white/10",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const delta = Math.floor((Date.now() - t) / 1000);
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return `${Math.floor(delta / 86400)}d ago`;
}

function StatusPill({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${STATUS_COLOURS[status] || STATUS_COLOURS.closed}`}>
      {status?.replace("_", " ") || "—"}
    </span>
  );
}

export function Tickets() {
  const toast = useToast();
  const [filter, setFilter] = useState("open");
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const load = () => {
    api.get(`/support/admin/tickets?status=${filter}`)
      .then(({ data }) => setList(data.tickets || []))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => { load(); }, [filter]);

  if (activeId) {
    return <AdminTicketThread id={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="admin-tickets-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <LifeBuoy size={16} className="text-[var(--teal)]" />
          Support Tickets
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors capitalize ${
                filter === s
                  ? "border-[var(--teal)] bg-[var(--teal)]/10 text-[var(--teal)]"
                  : "border-white/10 bg-white/5 text-[var(--muted)] hover:text-white/80"
              }`}
              data-testid={`admin-tickets-filter-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-[var(--muted)] glass-card p-4">No tickets in this status.</p>
      ) : (
        <ul className="space-y-2">
          {list.map((t) => (
            <li
              key={t.id}
              className="glass-card p-3 cursor-pointer hover:bg-white/[0.04] transition-colors"
              onClick={() => setActiveId(t.id)}
              data-testid={`admin-ticket-row-${t.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-[var(--muted)]">#{t.id}</span>
                    <span className="text-sm font-medium text-white/85 truncate">{t.subject}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-[var(--muted)] uppercase tracking-wider mt-0.5">
                    <span>{t.username || `user #${t.user_id}`}</span>
                    <span>{t.category}</span>
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare size={10} /> {t.msg_count}
                    </span>
                    <span>updated {relativeTime(t.updated_at)}</span>
                  </div>
                </div>
                <StatusPill status={t.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AdminTicketThread({ id, onBack }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/support/tickets/${id}`)
    .then(({ data }) => setData(data))
    .catch((e) => toast.error(apiError(e)));

  useEffect(() => { load(); }, [id]);

  const reply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    const fd = new FormData();
    fd.append("body", body.trim());
    if (file) fd.append("attachment", file);
    try {
      await api.post(`/support/tickets/${id}/reply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBody("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (newStatus) => {
    const fd = new FormData();
    fd.append("new_status", newStatus);
    try {
      await api.post(`/support/admin/tickets/${id}/status`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Marked as ${newStatus.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!data) return <div className="text-sm text-[var(--muted)]">Loading…</div>;
  const { ticket, messages } = data;

  return (
    <div className="space-y-4" data-testid={`admin-ticket-thread-${id}`}>
      <button onClick={onBack} className="text-[11px] uppercase tracking-wider text-[var(--muted)] hover:text-white/85">
        ← Back to tickets
      </button>

      <div className="glass-card p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-white/90">#{ticket.id} · {ticket.subject}</h3>
          <StatusPill status={ticket.status} />
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[var(--muted)] uppercase tracking-wider">
          <span>category: {ticket.category}</span>
          <span>opened {relativeTime(ticket.created_at)}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["open", "in_progress", "resolved", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              disabled={ticket.status === s}
              className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border border-white/10 bg-white/5 text-[var(--muted)] hover:text-white/85 disabled:opacity-40"
              data-testid={`admin-ticket-set-${s}`}
            >
              Mark {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <ul className="space-y-2">
        {messages.map((m) => {
          const isAdmin = m.sender_role === "admin";
          return (
            <li
              key={m.id}
              className={`glass-card p-3 ${isAdmin ? "border-[var(--gold)]/30" : "border-[var(--teal)]/20"}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-wider ${isAdmin ? "text-[var(--gold)]" : "text-[var(--muted)]"}`}>
                  {isAdmin ? "Admin" : "User"}
                </span>
                <span className="text-[10px] text-[var(--muted)]">{relativeTime(m.created_at)}</span>
              </div>
              <p className="text-[13px] text-white/90 whitespace-pre-wrap">{m.body}</p>
              {m.attachment_url && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`}
                  target="_blank" rel="noreferrer"
                  className="inline-block mt-2 text-[11px] text-[var(--teal)] hover:underline"
                >
                  view attachment
                </a>
              )}
            </li>
          );
        })}
      </ul>

      {ticket.status !== "closed" && (
        <form onSubmit={reply} className="glass-card p-4 space-y-3" data-testid="admin-ticket-reply-form">
          <textarea
            className="input min-h-[80px] resize-y"
            placeholder="Reply as admin…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={4000}
            data-testid="admin-ticket-reply-body"
          />
          <div className="flex items-center justify-between gap-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block text-xs text-[var(--muted)] file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/85 hover:file:bg-white/15"
            />
            <button
              type="submit"
              disabled={sending || !body.trim()}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="admin-ticket-reply-submit"
            >
              <Send size={14} />
              {sending ? "Sending…" : "Reply"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
